import React from 'react';
import { Box, Typography, Grid, IconButton, Dialog, DialogTitle, DialogContent, useTheme, useMediaQuery, Button, Divider, CircularProgress, Pagination } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { colors, fonts } from '../../ColorFont/color';
import { url } from '../../ConstUrl/Url';
import {
    IconChevronLeft,
    IconLeaf,
    IconEye,
    IconMapPin,
    IconAward,
    IconCalendar,
    IconTrendingUp,
    IconBox,
    IconSeeding,
    IconX,
    IconPlus
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';

const ProjectCard = ({ project, onInfoClick, tLocal }) => {
    const navigate = useNavigate();
    return (
        <Box sx={{
            p: '20px',
            background: colors.carbon_bg_card || '#FFFFFF',
            borderRadius: '16px',
            border: `1px solid ${colors.carbon_border_solid || '#E0E5EB'}`,
            boxShadow: colors.carbon_shadow_card || '0px 2px 4px rgba(0,0,0,0.05)',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            gap: '12px',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            cursor: 'pointer',
            flexShrink: 0,
            '&:hover': {
                boxShadow: '0px 12px 24px rgba(0,0,0,0.1)',
                transform: 'translateY(-4px)',
                borderColor: colors.carbon_bg_main || '#00B4D8'
            }
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                    <Typography sx={{
                        fontFamily: fonts.manrope || fonts.poppins,
                        fontWeight: 700,
                        fontSize: '15px',
                        color: colors.carbon_text_main || '#0F1729',
                        mb: '4px',
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                    }}>
                        {project.projectName || (project.village ? `${project.village} Project` : 'Carbon Project')}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <IconMapPin size={14} color="#65758B" />
                        <Typography sx={{
                            fontFamily: fonts.publicSans || fonts.poppins,
                            fontSize: '11px',
                            color: colors.carbon_text_secondary || '#65758B'
                        }}>
                            {project.district}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: '8px' }}>
                    <Button
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/carbon-credits/my-proofs?projectId=${project._id}`);
                        }}
                        size="small"
                        sx={{
                            textTransform: 'none',
                            fontFamily: fonts.publicSans || fonts.poppins,
                            fontWeight: 600,
                            fontSize: '11px',
                            color: colors.carbon_bg_main || '#00B4D8',
                            background: (colors.carbon_bg_main || '#00B4D8') + '10',
                            borderRadius: '8px',
                            px: '12px',
                            '&:hover': { background: (colors.carbon_bg_main || '#00B4D8') + '20' }
                        }}
                    >
                        {tLocal.viewProofs || "View Proofs"}
                    </Button>
                    <IconButton 
                        onClick={(e) => {
                            e.stopPropagation();
                            onInfoClick(project);
                        }}
                        sx={{ 
                            color: colors.carbon_text_secondary || '#65758B',
                            background: '#F1F5F9',
                            padding: '8px',
                            borderRadius: '10px',
                            '&:hover': { background: '#E2E8F0' }
                        }}
                    >
                        <IconEye size={18} stroke={2} />
                    </IconButton>
                </Box>
            </Box>

            <Grid container spacing={1}>
                <Grid item xs={6}>
                    <Typography sx={{ fontSize: '10px', color: '#65758B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', mb: '4px' }}>
                        Area
                    </Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#0F1729', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <IconBox size={14} color={colors.carbon_bg_main} /> {project.landSize} {project.unit}
                    </Typography>
                </Grid>
                <Grid item xs={6}>
                    <Typography sx={{ fontSize: '10px', color: '#65758B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', mb: '4px' }}>
                        Status
                    </Typography>
                    <Box sx={{ 
                        display: 'inline-flex', 
                        px: '8px', 
                        py: '2px', 
                        borderRadius: '6px', 
                        background: project.status === 'Verified' ? colors.primary_rgba_8 : '#FFF3E0',
                        color: project.status === 'Verified' ? '#2E7D32' : '#F57C00',
                        fontSize: '10px',
                        fontWeight: 800
                    }}>
                        {project.status || 'Under Review'}
                    </Box>
                </Grid>
            </Grid>

            <Divider sx={{ borderColor: '#F1F5F9', my: '4px' }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ fontSize: '12px', color: '#65758B', fontWeight: 500 }}>
                    Est. Carbon Credits
                </Typography>
                <Typography sx={{ 
                    fontFamily: fonts.manrope || fonts.poppins,
                    fontWeight: 800, 
                    fontSize: '18px', 
                    color: colors.carbon_bg_main || '#00B4D8' 
                }}>
                    +{project.estimatedCredits} <span style={{ fontSize: '11px', fontWeight: 600 }}>{tLocal.tCo2}</span>
                </Typography>
            </Box>
        </Box>
    );
};

const CarbonCreditsAllProjects = ({ userData, onBack }) => {
    const { i18n } = useTranslation();
    const currentLang = i18n.language || 'en';
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [projects, setProjects] = React.useState([]);
    const [selectedProject, setSelectedProject] = React.useState(null);
    const [openModal, setOpenModal] = React.useState(false);
    const [selectedProjectProofs, setSelectedProjectProofs] = React.useState([]);
    const [loadingProofs, setLoadingProofs] = React.useState(false);
    const [page, setPage] = React.useState(1);
    const [pageCount, setPageCount] = React.useState(1);
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        if (userData?._id) {
            setLoading(true);
            fetch(`${url}/api/carbon-credits/projects/${userData._id}?page=${page}`)
                .then(res => res.json())
                .then(result => {
                    const records = (result.projects || []).map(p => ({
                        ...p,
                        district: p.farmDetails?.district || '',
                        state: p.farmDetails?.state || '',
                        village: p.farmDetails?.city || p.farmDetails?.village || '',
                        landSize: p.farmDetails?.landSize || '',
                        unit: p.farmDetails?.unit || '',
                        ownershipType: p.farmDetails?.ownershipType || '',
                    }));
                    setProjects(records);
                    setPageCount(result.pageCount || 1);
                    setLoading(false);
                })
                .catch(err => {
                    console.error("Error fetching projects:", err)
                    setLoading(false);
                });
        }
    }, [userData, page]);

    const handleInfoClick = (project) => {
        setSelectedProject(project);
        setOpenModal(true);
        setLoadingProofs(true);
        fetch(`${url}/api/carbon-credits/proofs/project/${project._id}`)
            .then(res => res.json())
            .then(data => {
                setSelectedProjectProofs(data.proofs || []);
                setLoadingProofs(false);
            })
            .catch(err => {
                console.error("Error fetching proofs:", err);
                setLoadingProofs(false);
            });
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setSelectedProject(null);
    };

    // Use translations compatible with Dashboard
    const tLocal = {
        back: i18n.language === 'hi' ? "वापस" : i18n.language === 'mr' ? "मागे" : "Back",
        tCo2: "tCO₂",
        viewProofs: i18n.language === 'hi' ? "प्रमाण देखें" : i18n.language === 'mr' ? "पुरावे पहा" : "View Proofs"
    };

    return (
        <Box sx={{
            padding: 0,
            maxWidth: '1200px',
            margin: '0 auto',
            minHeight: '100vh',
        }}>
            {/* Page Header */}
            <Box sx={{ mb: '32px' }}>
                <Button
                    onClick={onBack || (() => navigate('/carbon-credits'))}
                    variant="outlined"
                    startIcon={<IconChevronLeft size={20} stroke={2.5} />}
                    sx={{
                        borderRadius: '50px',
                        px: 3,
                        height: '44px',
                        fontFamily: fonts.publicSans || fonts.poppins,
                        fontWeight: 700,
                        fontSize: '14px',
                        textTransform: 'none',
                        color: colors.carbon_text_main || '#1C252E',
                        borderColor: '#E0E5EB',
                        mb: '20px',
                        minWidth: '100px',
                        '&:hover': {
                            borderColor: '#919EAB',
                            backgroundColor: '#F4F6F8',
                        },
                    }}
                >
                    {tLocal.back || "Back"}
                </Button>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                    <CircularProgress sx={{ color: colors.carbon_bg_main }} />
                </Box>
            ) : (
                <>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        {projects.length > 0 ? (
                            <>
                                {projects.map((project) => (
                                    <ProjectCard project={project} onInfoClick={handleInfoClick} tLocal={tLocal} key={project._id} />
                                ))}
                                
                                {pageCount > 1 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
                                        <Pagination 
                                            count={pageCount}
                                            page={page}
                                            onChange={(e, v) => setPage(v)}
                                            color="primary"
                                            sx={{
                                                '& .MuiPaginationItem-root': {
                                                    fontWeight: 700,
                                                    borderRadius: '8px',
                                                    fontFamily: fonts.poppins,
                                                    color: colors.carbon_text_main,
                                                }
                                            }}
                                        />
                                    </Box>
                                )}
                            </>
                        ) : (
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                py: 8,
                                gap: '16px',
                                border: `2px dashed ${colors.carbon_border_dashed || '#E2E8F0'}`,
                                borderRadius: '20px',
                                background: 'rgba(248,250,252,0.5)',
                            }}>
                                <Box sx={{
                                    width: '72px', height: '72px', borderRadius: '50%',
                                    background: 'rgba(0,180,216,0.08)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    color: colors.carbon_bg_main || '#00B4D8',
                                }}>
                                    <IconLeaf size={36} stroke={1.5} />
                                </Box>
                                <Box sx={{ textAlign: 'center', px: '24px' }}>
                                    <Typography sx={{
                                        fontFamily: fonts.manrope || fonts.poppins,
                                        fontWeight: 700, fontSize: '18px',
                                        color: colors.carbon_text_main || '#0F1729', mb: '8px',
                                    }}>
                                        No carbon projects found
                                    </Typography>
                                    <Typography sx={{
                                        fontFamily: fonts.publicSans || fonts.poppins,
                                        fontSize: '14px', color: '#64748B', lineHeight: '22px',
                                    }}>
                                        Register a project to start earning carbon credits
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </>
            )}

            {/* Modal */}
            <Dialog 
                open={openModal} 
                onClose={handleCloseModal}
                fullScreen={true}
                PaperProps={{
                    sx: { borderRadius: 0 }
                }}
            >
                <DialogTitle sx={{ 
                    p: '24px',
                    borderBottom: `1px solid ${colors.carbon_border_solid || '#E0E5EB'}`,
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    fontFamily: fonts.manrope || fonts.poppins,
                    fontWeight: 700,
                    fontSize: '20px'
                }}>
                    Project Details
                    <IconButton onClick={handleCloseModal} sx={{ color: '#65758B' }}>
                        <IconX size={24} />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: '24px' }}>
                    {selectedProject && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                            <Box sx={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                                <Box sx={{ 
                                    width: '48px', height: '48px', borderRadius: '12px', 
                                    backgroundColor: colors.carbon_bg_light || '#B2EBF2',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#00838F'
                                }}>
                                    <IconLeaf size={24} />
                                </Box>
                                <Box>
                                    <Typography sx={{ fontSize: '13px', color: '#65758B', mb: '2px', fontWeight: 500, textTransform: 'uppercase' }}>Project Name</Typography>
                                    <Typography sx={{ fontWeight: 700, fontSize: '18px', color: colors.carbon_text_main }}>{selectedProject.projectName || (selectedProject.village ? `${selectedProject.village} Project` : 'Carbon Project')}</Typography>
                                </Box>
                            </Box>

                            <Divider />

                            <Box sx={{ display: 'flex', gap: '16px' }}>
                                <IconMapPin size={20} color="#65758B" style={{ marginTop: '4px' }} />
                                <Box>
                                    <Typography sx={{ fontSize: '13px', color: '#65758B', mb: '4px', fontWeight: 600 }}>FARM LOCATION</Typography>
                                    <Typography sx={{ fontWeight: 500, fontSize: '15px' }}>{selectedProject.village}, {selectedProject.district}, {selectedProject.state}</Typography>
                                </Box>
                            </Box>

                            <Grid container spacing={3}>
                                <Grid item xs={6}>
                                    <Box sx={{ display: 'flex', gap: '12px' }}>
                                        <IconBox size={20} color="#65758B" />
                                        <Box>
                                            <Typography sx={{ fontSize: '13px', color: '#65758B', mb: '4px', fontWeight: 600 }}>LAND SIZE</Typography>
                                            <Typography sx={{ fontWeight: 600, fontSize: '15px' }}>{selectedProject.landSize} {selectedProject.unit}</Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                                <Grid item xs={6}>
                                    <Box sx={{ display: 'flex', gap: '12px' }}>
                                        <IconAward size={20} color="#65758B" />
                                        <Box>
                                            <Typography sx={{ fontSize: '13px', color: '#65758B', mb: '4px', fontWeight: 600 }}>OWNERSHIP</Typography>
                                            <Typography sx={{ fontWeight: 600, fontSize: '15px' }}>{selectedProject.ownershipType || 'N/A'}</Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                                <Grid item xs={6}>
                                    <Box sx={{ display: 'flex', gap: '12px' }}>
                                        <IconCalendar size={20} color="#65758B" />
                                        <Box>
                                            <Typography sx={{ fontSize: '13px', color: '#65758B', mb: '4px', fontWeight: 600 }}>DURATION</Typography>
                                            <Typography sx={{ fontWeight: 600, fontSize: '15px' }}>{selectedProject.projectDuration || 'N/A'}</Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                                <Grid item xs={6}>
                                    <Box sx={{ display: 'flex', gap: '12px' }}>
                                        <IconTrendingUp size={20} color="#65758B" />
                                        <Box>
                                            <Typography sx={{ fontSize: '13px', color: '#65758B', mb: '4px', fontWeight: 600 }}>STATUS</Typography>
                                            <Typography sx={{ fontWeight: 700, fontSize: '15px', color: selectedProject.status === 'Verified' ? '#2E7D32' : '#F57C00' }}>
                                                {selectedProject.status || 'Under Review'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>

                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mb: '12px' }}>
                                    <IconSeeding size={20} color="#65758B" />
                                    <Typography sx={{ fontSize: '13px', color: '#65758B', fontWeight: 600 }}>SELECTED PRACTICES</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', pl: '32px' }}>
                                    {selectedProject.practices?.map((p, i) => (
                                        <Box key={i} sx={{ 
                                            px: '14px', py: '6px', borderRadius: '50px', 
                                            background: '#F1F5F9', fontSize: '12px', fontWeight: 600,
                                            color: '#334155', border: '1px solid #E2E8F0'
                                        }}>
                                            {p}
                                        </Box>
                                    ))}
                                    {(!selectedProject.practices || selectedProject.practices.length === 0) && (
                                        <Typography sx={{ fontSize: '14px', color: '#94A3B8', fontStyle: 'italic' }}>No practices listed</Typography>
                                    )}
                                </Box>
                            </Box>

                            <Box sx={{ 
                                mt: '8px',
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center',
                                p: '20px', 
                                borderRadius: '16px', 
                                background: `linear-gradient(135deg, ${colors.carbon_bg_light || '#E0F7FA'} 0%, #FFFFFF 100%)`,
                                border: `1px solid ${colors.carbon_border_solid || '#E0E5EB'}`,
                                boxShadow: '0px 4px 12px rgba(0,0,0,0.03)',
                                mb: '40px'
                            }}>
                                <Box>
                                    <Typography sx={{ fontWeight: 700, color: colors.carbon_text_main, fontSize: '15px' }}>Estimated Carbon Credits</Typography>
                                    <Typography sx={{ fontSize: '12px', color: '#65758B' }}>Based on current farming data</Typography>
                                </Box>
                                <Typography sx={{ 
                                    fontFamily: fonts.manrope || fonts.poppins,
                                    fontWeight: 800, 
                                    fontSize: '24px', 
                                    color: colors.carbon_bg_main || '#00B4D8' 
                                }}>
                                    +{selectedProject.estimatedCredits} <span style={{ fontSize: '14px', fontWeight: 600 }}>{tLocal.tCo2}</span>
                                </Typography>
                            </Box>

                            {/* Upload Proof Button */}
                            {!loadingProofs && selectedProjectProofs.length === 0 && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center' }}>
                                    <Button
                                        onClick={() => {
                                            handleCloseModal();
                                            navigate(`/carbon-credits/upload-proof?projectId=${selectedProject._id}`);
                                        }}
                                        variant="contained"
                                        startIcon={<IconPlus size={20} />}
                                        sx={{
                                            background: colors.carbon_bg_main || '#00B4D8',
                                            color: '#FFFFFF',
                                            borderRadius: '14px',
                                            padding: '12px 48px',
                                            textTransform: 'none',
                                            fontFamily: fonts.publicSans || fonts.poppins,
                                            fontWeight: 700,
                                            fontSize: '16px',
                                            boxShadow: '0px 8px 16px rgba(0, 180, 216, 0.2)',
                                            '&:hover': {
                                                background: '#009DC0',
                                                transform: 'translateY(-2px)',
                                                boxShadow: '0px 12px 20px rgba(0, 180, 216, 0.3)',
                                            },
                                            transition: 'all 0.3s ease',
                                            width: isMobile ? '100%' : 'fit-content'
                                        }}
                                    >
                                        Upload Project Proofs
                                    </Button>
                                    <Typography sx={{ fontSize: '13px', color: '#94A3B8', textAlign: 'center' }}>
                                        Adding proof helps in faster project verification
                                    </Typography>
                                </Box>
                            )}
                            {loadingProofs && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                    <CircularProgress size={24} />
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default CarbonCreditsAllProjects;
